import { useCallback, useState, useEffect } from 'react';

import { ArrowDownToLineIcon, ArrowLeftToLineIcon, ArrowRightToLineIcon, ArrowUpToLineIcon, CornerDownLeft, CornerLeftUp, CornerRightDown, CornerUpRight, MoveHorizontalIcon, MoveVerticalIcon } from 'lucide-react';
import { Input, InputGroup, InputGroupText } from 'twyne-new.ui';

import { useCraftProp } from '@/components/builder/utils';

import { UNIT_PREDEFINED_OPTIONS } from '../utils/predefined_sizes';

import SelectUnit, { getUnit as extractUnit, removeUnit } from './SelectUnit';
import ToggleGroup from './ToggleGroup';

const ICONS = {
  t: ArrowUpToLineIcon,
  r: ArrowRightToLineIcon,
  b: ArrowDownToLineIcon,
  l: ArrowLeftToLineIcon,
  x: MoveHorizontalIcon,
  y: MoveVerticalIcon,
  tl: CornerUpRight,
  tr: CornerRightDown,
  br: CornerDownLeft,
  bl: CornerLeftUp,
}

export default function SpacingProperty({ name, options, predefined, inputValue: InputValue = DefaultInputValue, defaultUnit, unitOptions = UNIT_PREDEFINED_OPTIONS /* negative */ }) {
  const { value: sizes, setValue } = useCraftProp(name)
  const [itens, setItems] = useState({ ...options[0] })
  const [unit, setUnit] = useState(defaultUnit)

  const getValue = useCallback(prop => sizes?.[prop], [sizes])
  const getUnit = newUnit => !newUnit || newUnit === 'predefined' ? '' : newUnit
  const showToggle = options.length > 1

  useEffect(() => {
    if (!sizes) return

    const matchingOption = options.find(option => {
      const optionTypes = option.value.map(item => item.type)
      const sizeTypes = Object.keys(sizes)

      return sizeTypes.every(type => optionTypes.includes(type))
    })

    if (matchingOption) {
      setItems(matchingOption)
    }
  }, [sizes, options])

  const handleChangeSize = prop => ({ value, newUnit }) => {
    const unit = isNaN(value) ? '' : getUnit(newUnit)
    const newValue = value ? value + unit : ''
    setValue({
      ...sizes,
      [prop]: newValue,
    })
  }

  const handleToggle = useCallback(newToggle => {
    if (!newToggle) {
      return
    }

    const newItemValue = options.find(item => item.label === newToggle)

    setItems(newItemValue)
    setValue(newItemValue.value.reduce((acc, item) => {
      acc[item.type] = sizes?.[item.type]
      return acc
    }, {}))
  }, [setItems, setValue, sizes, options])

  const handleChangeUnit = newUnit => {
    const copySize = { ...sizes }
    itens.value.forEach(item => {
      const value = removeUnit(getValue(item.type))
      const newValue = value ? value + getUnit(newUnit) : ''
      copySize[item.type] = newValue
    })

    setUnit(newUnit)
    setValue(copySize)
  }

  useEffect(() => {
    if (sizes && unit === undefined) {
      const newUnit = extractUnit(getValue(itens.value[0].type))
      setUnit(newUnit)
    }
  }, [sizes, unit, getValue, itens])

  const toggleOptions = options.map(({ label }) => ({
    value: label,
    icon: label,
    tooltip: label,
  }))

  return <>
    {showToggle && <>
      <ToggleGroup
        name={name}
        options={toggleOptions}
        type='single'
        className='mb-2'
        value={itens.label}
        onValueChange={handleToggle}
      />
    </>}

    {itens.value.length === 1 ? <>
      <InputGroup variant='accent'>
        <InputValue
          disabled={itens.value.length !== 1}
          value={removeUnit(getValue(itens.value[0].type))}
          onChange={value => handleChangeSize(itens.value[0].type)({ value, newUnit: unit })}
          predefined={predefined}
          unit={unit}
          onChangeUnit={handleChangeUnit}
          className={predefined ? 'w-full' : 'w-3/5'}
        />

        {!predefined && <>
          <SelectUnit
            value={getValue(itens.value[0].type)}
            unit={unit}
            onChangeUnit={handleChangeUnit}
            options={unitOptions}
            variant='accent'
          />
        </>}

      </InputGroup>
    </> : <SelectUnit
      value={getValue(itens.value[0].type)}
      unit={unit}
      onChangeUnit={handleChangeUnit}
      options={unitOptions}
      variant='accent'
      onde='spacing-itens maior que 1'
    />}

    {itens.value.length > 1 && <div className='flex flex-wrap gap-1 mt-1'>
      {itens.value.map(({ type: prefix, value: key }) => <>
        <InputGroup key={prefix} variant='accent' className='basis-[calc(50%-0.125rem)]'>
          <InputGroupText addonType='prepend' className='twy-text-base-neutral p-1'>
            <Icon value={key} />
          </InputGroupText>
          <InputValue
            multiple
            onChangeUnit={handleChangeUnit}
            onChange={value => handleChangeSize(prefix)({ value, newUnit: unit })}
            value={removeUnit(getValue(prefix))}
          />
        </InputGroup>
      </>)}
    </div>}
  </>
}

function DefaultInputValue({ onChange, disabled, value, ...props }) {
  return <>
    <Input
      variant='accent'
      type='number'
      size='sm'
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      value={!disabled ? value : ''}
      onKeyDown={e => e.key === ',' && e.preventDefault()}
      {...props}
    />
  </>
}

function Icon({ value }) {
  const Component = ICONS[value]
  return <Component size={14} />
}
